import type { ScheduleReminder } from '@/types/cycle-evaluation/CycleSchedule';

import { readRefusal } from '@/api/api';
import { getReminderOffsetsOutsideCompletionWindow } from '@/utils/schedule';

const SCHEDULE_REMINDERS_FIELD_PREFIX = 'schedulereminders';

interface GetRefusedReminderOffsetsParams {
    error: unknown;
    scheduleReminders: readonly ScheduleReminder[];
    daysAllowedToComplete?: number;
}

/**
 * The server answers this refusal with `ValidationProblemDetails` keyed on the C# property path
 * (`ScheduleReminders[0]`) and a developer-English sentence. The key is the stable half, so it is
 * what we read; the offsets to name are recomputed locally against the same rule.
 */
const isScheduleRemindersRefusal = (error: unknown): boolean => {
    const refusal = readRefusal(error);

    return (
        refusal?.status === 400 &&
        Object.keys(refusal.fieldErrors).some((field) =>
            field.toLowerCase().startsWith(SCHEDULE_REMINDERS_FIELD_PREFIX),
        )
    );
};

const getRefusedReminderOffsets = ({
    error,
    scheduleReminders,
    daysAllowedToComplete,
}: GetRefusedReminderOffsetsParams): number[] => {
    if (!isScheduleRemindersRefusal(error)) {
        return [];
    }

    return getReminderOffsetsOutsideCompletionWindow({
        scheduleReminders,
        daysAllowedToComplete,
    });
};

export { getRefusedReminderOffsets, isScheduleRemindersRefusal };
export type { GetRefusedReminderOffsetsParams };
